import React, {useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {ProductKey, ProductPlan, ProductPlanTier} from '../../services/cellcast.v2.types'
import PlanPaymentForm from './PlanPaymentForm'
import './FeatureActivatePage.scss'
import PaymentSuccessModal from '../pages/UserPages/wallet-card/PaymentSuccessModal'
import {useI18n} from '../Utils/_helpers/useI18n'
import { manageButtonText } from '../pages/UserPages/accounts/Billing/BillingSubscription'

type FeatureActivatePageProps = {
  title?: string
  plans: ProductPlan[]
  onSelectPlan?: (plan: ProductPlan) => Promise<void>
  showBackButton?: boolean
  setupTime?: number
}

const FeatureActivatePage: React.FC<FeatureActivatePageProps> = ({
  title,
  plans,
  onSelectPlan,
  showBackButton = true,
  setupTime,
}) => {
  const {t} = useI18n()
  const navigate = useNavigate()
  const [showPaymentForm, setShowPaymentForm] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<ProductPlan | null>(null)
  const [showPaymentSuccessModal, setShowPaymentSuccessModal] = useState(false)
  const handleBack = () => {
    navigate(-1)
  }

  const handlePlanSelection = async (plan: ProductPlan) => {
    setSelectedPlan(plan)
    setShowPaymentForm(true)
  }

  const handlePaymentSuccess = async () => {
    setShowPaymentForm(false)

    if (selectedPlan && onSelectPlan) {
      await onSelectPlan(selectedPlan)
      setSelectedPlan(null)
    }
  }

  const sortedPlans = (plans && Array.isArray(plans) ? [...plans] : [])?.sort((a, b) => {
    if (a.planKey === ProductPlanTier.FREE) return -1
    if (b.planKey === ProductPlanTier.FREE) return 1
    return (a.price || 0) - (b.price || 0)
  })

  const isPopular = (planKey: string) => {
    return planKey === ProductPlanTier.PRO
  }

  const getButtonText = (planKey: string, productKey: ProductKey) => {
    switch (planKey) {
      case ProductPlanTier.FREE:
        return t('COMMON.START_FREE')
      case ProductPlanTier.BASIC:
        return t('COMMON.ACTIVATE_BASIC')
      case ProductPlanTier.PRO:
        return t('COMMON.GO_PRO')
      case ProductPlanTier.BUSINESS:
      case ProductPlanTier.ENTERPRISE:
        return productKey === ProductKey.LIVECHAT ? t('COMMON.ACTIVATE_NOW_LIVECHAT') : t('COMMON.SCALE_NOW')
      default:
        return t('COMMON.CHOOSE_PLAN')
    }
  }

  const isLoadingPlans = !plans || plans.length === 0

  return (
    <div className='feature-activate-page'>
      {showBackButton && (
        <button
          type='button'
          className='btn btn-light-primary mb-4 back-button'
          onClick={handleBack}
        >
          <i className='bi bi-arrow-left me-2'></i>
          {t('COMMON.BACK')}
        </button>
      )}

      <div className='header-section'>
        {isLoadingPlans ? (
          <>
            <div className='placeholder-glow mb-3'>
              <div className='placeholder col-6'></div>
            </div>
            <div className='placeholder-glow'>
              <div className='placeholder col-8'></div>
            </div>
          </>
        ) : (
          <>
            <h1 className='main-title'>{title}</h1>
            <p className='subtitle'>
              Every plan starts with a{' '}
              <span className='highlight'>{t('COMMON.14_DAY_FREE_TRIAL')}</span> - no risk, guided
              setup available.
            </p>

            <div className='setup-time'>
              <i className='bi bi-clock clock-icon'></i>
              {t('COMMON.SETUP_TAKES_LESS_THAN_MINUTES', {minutes: setupTime || 2})}
            </div>
          </>
        )}
      </div>

      {/* Pricing Cards */}
      <div className='pricing-cards-container row justify-content-center'>
        {isLoadingPlans
          ? new Array(3).fill(null).map((_, idx) => (
              <div key={`skeleton-${idx}`} className='col-12 col-md-6 col-lg-4 col-xl-3'>
                <div className='pricing-card'>
                  <div className='card-body'>
                    <div className='placeholder-glow mb-3'>
                      <div className='placeholder col-6'></div>
                    </div>
                    <div className='placeholder-glow mb-4'>
                      <div className='placeholder col-4'></div>
                    </div>
                    <ul className='mt-4 mb-4 flex-grow-1' style={{listStyle: 'none', padding: 0}}>
                      {new Array(4).fill(null).map((__, i) => (
                        <li key={i} className='mb-3 d-flex align-items-center'>
                          <span className='symbol symbol-24px me-3'>
                            <span
                              className='placeholder rounded-circle'
                              style={{width: 24, height: 24}}
                            ></span>
                          </span>
                          <div className='placeholder col-7'></div>
                        </li>
                      ))}
                    </ul>
                    <div className='placeholder col-12' style={{height: 38, borderRadius: 6}}></div>
                  </div>
                </div>
              </div>
            ))
          : sortedPlans.map((plan, idx) => {
              const productKey = plan.productId?.productKey as ProductKey
              const planKey = plan.planKey as string
              const popular = isPopular(planKey)
              return (
                <div key={`${planKey}-${idx}`} className='col-12 col-md-6 col-lg-4 col-xl-3'>
                  <div className={`pricing-card ${popular ? 'popular' : ''}`}>
                    {popular && <div className='popular-badge'>{t('COMMON.MOST_POPULAR')}</div>}
                    <div className='card-body'>
                      <h2 className='plan-title'>
                        {planKey.charAt(0).toUpperCase() + planKey.slice(1)}
                      </h2>
                      <div className='price-section'>
                        <span className='price-amount'>${Math.floor(plan.price || 0)}</span>
                        <span className='price-period'>/mo</span>
                      </div>
                      <p className='plan-description'>{plan?.description}</p>
                      <ul className='features-list'>
                        {plan.features &&
                          plan.features.length > 0 &&
                          plan.features.slice(0, 6).map((feature: string, i: number) => (
                            <li key={i} className='feature-item'>
                              <i className='bi bi-check-circle-fill check-icon-1'></i>
                              <span className='feature-text'>{feature}</span>
                            </li>
                          ))}
                      </ul>
                      <button
                        className={`plan-button ${
                          planKey === ProductPlanTier.FREE ? 'secondary' : 'primary'
                        }`}
                        onClick={() => handlePlanSelection(plan)}
                      >
                        {getButtonText(planKey, productKey)}
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
      </div>

      <div className='benefits-section'>
        {isLoadingPlans ? (
          <>
            <div className='text-center mb-10'>
              <div className='placeholder-glow mb-2'>
                <div className='placeholder col-6 mx-auto' style={{height: 28}}></div>
              </div>
              <div className='placeholder-glow'>
                <div className='placeholder col-8 mx-auto'></div>
              </div>
            </div>
            <div className='benefits-grid row'>
              {new Array(3).fill(null).map((_, i) => (
                <div key={`benefit-skel-${i}`} className='col-md-4'>
                  <div className='benefit-card'>
                    <div className='benefit-icon'></div>
                    <div className='placeholder-glow mb-2'>
                      <div className='placeholder col-8 mx-auto' style={{height: 20}}></div>
                    </div>
                    <div className='placeholder-glow'>
                      <div className='placeholder col-9 mx-auto'></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className='section-header'>
              <h2 className='section-title'>
                {t('COMMON.EVERYTHING_YOU_NEED_TO_SUCCEED_RISK_FREE')}
              </h2>
              <div className='section-subtitle'>
                We're committed to your success with comprehensive support and guarantees
              </div>
            </div>
            <div className='benefits-grid row'>
              <div className='col-md-4'>
                <div className='benefit-card'>
                  <div className='benefit-icon clock'>
                    <i className='bi bi-clock font-icon'></i>
                  </div>
                  <div className='benefit-title'>{t('COMMON.14_DAY_FREE_TRIAL_ON_ALL_PLANS')}</div>
                  <div className='benefit-description'>Cancel anytime - no setup fees</div>
                </div>
              </div>
              <div className='col-md-4'>
                <div className='benefit-card'>
                  <div className='benefit-icon people'>
                    <i className='bi bi-people font-icon'></i>
                  </div>
                  <div className='benefit-title'>
                    {t('COMMON.GUIDED_ONBOARDING_WITH_YOUR_ACCOUNT_MANAGER')}
                  </div>
                  <div className='benefit-description'>
                    Personal support to help you launch fast
                  </div>
                </div>
              </div>
              <div className='col-md-4'>
                <div className='benefit-card'>
                  <div className='benefit-icon shield'>
                    <i className='bi bi-shield font-icon'></i>
                  </div>
                  <div className='benefit-title'>{t('COMMON.30_DAY_MONEY_BACK_GUARANTEE')}</div>
                  <div className='benefit-description'>Switch plans anytime - 24/7 support</div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      {/* Trust badges */}
      <div className='trust-badges'>
        <div className='trust-item'>
          <i className='bi bi-shield-check trust-icon'></i>
          <span>{t('COMMON.ENTERPRISE_SECURITY')}</span>
        </div>
        <span className='divider'>•</span>
        <div className='trust-item'>
          <i className='bi bi-people trust-icon'></i>
          <span>{t('COMMON.50_000_PLUS_HAPPY_USERS')}</span>
        </div>
        <span className='divider'>•</span>
        <div className='trust-item'>
          <i className='bi bi-star trust-icon'></i>
          <span>{t('COMMON.4_9_5_RATING')}</span>
        </div>
      </div>

      <PlanPaymentForm
        show={showPaymentForm}
        onClose={() => {
          setShowPaymentForm(false)
          setSelectedPlan(null)
        }}
        plan={selectedPlan}
        onPaymentSuccess={() => {
          handlePaymentSuccess()
        }}
      />

      <PaymentSuccessModal
        show={showPaymentSuccessModal}
        onClose={() => setShowPaymentSuccessModal(false)}
        plan={selectedPlan}
        description={`${t(
          'WALLET_CARD.CLICK_ON_THE_BUTTON_BELOW_TO_START_CREATING_YOUR_DIGITAL_BUSINESS_CARDS'
        )}`}
        onGoToSubscriptions={() => {
          navigate('/live-chatbot/subscription')
        }}
        titleGoto={manageButtonText(selectedPlan)}
      />
    </div>
  )
}

export default FeatureActivatePage
